import {
  ApiError,
  HttpError,
  NetworkError,
  RequestCancelledError,
  RequestPayloadValidationError,
  ResponsePayloadValidationError,
} from '@hprofits/api-client/errors';
import { HpError } from '@hprofits/shared/errors';
import type { Optional } from '@hprofits/utils/fp/optional/types';
import * as Sentry from '@sentry/vue';
import { z } from 'zod';

import type { ErrorWithDetails } from '@/utils/errors';
import { isErrorWithDetails } from '@/utils/errors';

const UNKNOWN_REQUEST_URL = 'unknown';
const SERVER_ERROR_STATUS_THRESHOLD = 500;

const stripSensitiveUrlParts = (url: string): string => {
  const hashlessUrl = url.split('#')[0];
  return hashlessUrl?.split('?')[0] ?? '';
};

const getSanitizedRequestUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return stripSensitiveUrlParts(url) || UNKNOWN_REQUEST_URL;
  }
};

/** Extract pathname from URL and replace dynamic IDs for Sentry fingerprinting: /campaigns/123 → /campaigns/:id */
const normalizeUrlPath = (url: string): string => {
  const sanitizedUrl = getSanitizedRequestUrl(url);
  let path: string;

  try {
    path = new URL(sanitizedUrl).pathname;
  } catch {
    path = sanitizedUrl;
  }

  return path
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d+/g, '/:id');
};

type BasicTags = Record<string, Optional<string>>;

const captureApiError = (error: ApiError, basicTags: BasicTags): void => {
  const level = error.status >= SERVER_ERROR_STATUS_THRESHOLD ? 'error' : 'warning';
  const sanitizedUrl = getSanitizedRequestUrl(error.url);
  const normalizedPath = normalizeUrlPath(error.url);
  Sentry.addBreadcrumb({
    type: 'http',
    category: 'fetch',
    level,
    data: {
      method: error.method,
      url: sanitizedUrl,
      status_code: error.status,
      reason: error.message,
    },
  });
  Sentry.captureException(error, {
    level,
    fingerprint: ['api-error', error.method, normalizedPath, String(error.status), error.errorCode],
    tags: {
      ...basicTags,
      httpStatus: error.status,
      httpMethod: error.method,
      apiPath: normalizedPath,
      errorType: 'api-error',
      errorCode: error.errorCode,
      apiErrorId: error.errorId,
      apiErrorTimestamp: error.timestamp,
    },
    contexts: {
      request: { method: error.method, url: sanitizedUrl },
      response: {
        status_code: error.status,
        error_id: error.errorId,
        error_code: error.errorCode,
        error_message: error.message,
        error_timestamp: error.timestamp,
        error_data: error.data,
      },
    },
    extra: {
      errorId: error.errorId,
      errorTimestamp: error.timestamp,
      data: error.data,
    },
  });
};

const captureHttpError = (error: HttpError, basicTags: BasicTags): void => {
  const sanitizedUrl = getSanitizedRequestUrl(error.url);
  const normalizedPath = normalizeUrlPath(error.url);
  Sentry.addBreadcrumb({
    type: 'http',
    category: 'fetch',
    level: 'error',
    data: { method: error.method, url: sanitizedUrl, status_code: error.status, reason: error.message },
  });
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['http-error', error.method, normalizedPath, String(error.status)],
    tags: {
      ...basicTags,
      httpStatus: error.status,
      httpMethod: error.method,
      apiPath: normalizedPath,
      errorType: 'http-error',
    },
    contexts: {
      request: { method: error.method, url: sanitizedUrl },
      response: { status_code: error.status, error_message: error.message },
    },
  });
};

const captureResponseValidationError = (error: ResponsePayloadValidationError, basicTags: BasicTags): void => {
  const sanitizedUrl = getSanitizedRequestUrl(error.url);
  const normalizedPath = normalizeUrlPath(error.url);
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['response-payload-validation-error', error.method, normalizedPath],
    tags: {
      ...basicTags,
      httpMethod: error.method,
      apiPath: normalizedPath,
      errorType: 'response-payload-validation-error',
    },
    contexts: {
      request: { method: error.method, url: sanitizedUrl },
    },
    extra: {
      prettyIssues: z.prettifyError(error.validationError),
      issues: error.validationError.issues,
    },
  });
};

const captureRequestValidationError = (error: RequestPayloadValidationError, basicTags: BasicTags): void => {
  const sanitizedUrl = getSanitizedRequestUrl(error.url);
  const normalizedPath = normalizeUrlPath(error.url);
  const issuePaths = error.validationError.issues
    .map((issue) => (issue.path ?? []).join('.'))
    .filter(Boolean)
    .sort()
    .join(',');
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['request-payload-validation-error', error.method, normalizedPath, issuePaths || 'unknown-path'],
    tags: {
      ...basicTags,
      httpMethod: error.method,
      apiPath: normalizedPath,
      errorType: 'request-payload-validation-error',
    },
    contexts: {
      request: { method: error.method, url: sanitizedUrl },
    },
    extra: {
      issues: error.validationError.issues,
      prettyIssues: z.prettifyError(error.validationError),
    },
  });
};

const captureHpError = (error: ErrorWithDetails, basicTags: BasicTags): void => {
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['error', error.name],
    tags: { ...basicTags, errorName: error.name },
    extra: { details: error.details },
  });
};

const captureGenericError = (error: Error, basicTags: BasicTags): void => {
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['error', error.name],
    tags: { ...basicTags, errorName: error.name },
  });
};

const captureUnknownError = (error: unknown, basicTags: BasicTags): void => {
  Sentry.captureException(error, {
    level: 'error',
    fingerprint: ['unknown-error'],
    tags: { ...basicTags, errorName: 'UnknownError' },
  });
};

export const writeErrorToSentry = (error: unknown, context?: string): void => {
  if (error instanceof RequestCancelledError) {
    // We don't want to report canceled requests to Sentry'
    return;
  }
  if (error instanceof NetworkError) {
    // If there is no connectivity - we can't send errors to sentry.
    return;
  }

  const basicTags: BasicTags = { apiClient: 'v2', context };

  if (error instanceof ApiError) {
    captureApiError(error, basicTags);
  } else if (error instanceof HttpError) {
    captureHttpError(error, basicTags);
  } else if (error instanceof ResponsePayloadValidationError) {
    captureResponseValidationError(error, basicTags);
  } else if (error instanceof RequestPayloadValidationError) {
    captureRequestValidationError(error, basicTags);
  } else if (error instanceof HpError && isErrorWithDetails(error)) {
    captureHpError(error, basicTags);
  } else if (error instanceof Error) {
    captureGenericError(error, basicTags);
  } else {
    captureUnknownError(error, basicTags);
  }
};
